import React, { useState, useEffect, useContext, useCallback } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { initSchedule } from "../../utils/data/initData";
import { useErrorDialog } from "../../contexts/ErrorDialogContext";
import { useSuccessDialog } from "../../contexts/SuccessDialogContext";
import ComponentLoading from "../../components/App/ComponentLoading";
import { handleScheduleDeletion } from "./utils/handleScheduleDeletion";
import BreakdownSchedule from "../../components/Schedules/Breakdown/BreakdownSchedule";
import { useAccessControl } from "../../utils/hooks/useAccessControl";
import { BREAKDOWN_SCHEDULES } from "../../utils/data/controlData";

const FullBreakdownPage = ({
  company,
  scheduleId,
  openScheduleWithDelay,
}) => {
  const { updatePLSchedule, getScheduleData, deleteScheduleByScheduleType } =
    useContext(AuthContext);
  const [schedule, setSchedule] = useState({});
  const [scheduleDetails, setScheduleDetails] = useState({});

  const { canEdit: canEditBreakdownSchedule } =
    useAccessControl(BREAKDOWN_SCHEDULES);

  const [loading, setLoading] = useState(true);

  const handleError = useErrorDialog();
  const handleSuccess = useSuccessDialog();

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getScheduleData(scheduleId, 'Full_Breakdown');

      const scheduleData = response.schedule;
      if (scheduleData) {
        setSchedule(initSchedule(scheduleData, "Full Breakdown"));
        setScheduleDetails({
          rows: scheduleData.rows.map((item) => ({
            row_id: item.row_id,
            name: item.description || item.name,
            amount: item.amount.toString(),
            historyAmount: item.historyAmount || [],
            taxAdjustment:
              typeof item.taxAdjustment === "object"
                ? item.taxAdjustment.value ?? ""
                : item.taxAdjustment ?? "",
            reference: item.reference,
            schedule:
              typeof item.taxAdjustment === "object"
                ? item.taxAdjustment.sch ?? ""
                : "",
            source: item.source,
            status: item.status || null,
            balance: "",
            permitToDelete: true,
          })),
          sourceData: scheduleData.sources,
          totalSourceAmount: scheduleData.totalSourceAmount,
        });
      }
    } catch (error) {
      handleError(
        "An unexpected error occurred while loading data. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!company || !scheduleId) {
      handleError(
        "We encountered an issue while fetching the data. Please refresh the page or try again shortly."
      );
      return;
    }

    fetchData();
  }, [scheduleId, company.uen]);

  const saveSchedule = async (
    schedule,
    scheduleDetails,
    fallbackSuccessMsg = "The schedule has been saved successfully."
  ) => {
    if (!company || !scheduleId) {
      handleError("Unexpected error happens. Please try again later.");
      return;
    }

    try {
      setLoading(true);
      const updatedSchedule = {
        sch_id: scheduleId,
        scheduleType: "FullBreakdown",
        scheduleTitle: schedule.name,
        rows: scheduleDetails.rows,
        notes: schedule.notes,
        reviewNotes: schedule.reviewNotes,
        totalAdjustment: scheduleDetails.totalAdjustment,
        doc: scheduleDetails.doc,
      };

      await updatePLSchedule(updatedSchedule);
      handleSuccess(fallbackSuccessMsg);

      await fetchData();
    } catch (error) {
      handleError(
        "There was a problem saving the schedule. Please contact support if the issue persists."
      );
    } finally {
      setLoading(false);
    }
  };

  const deleteSchedule = async () => {
    await handleScheduleDeletion({
      scheduleId,
      deleteFn: deleteScheduleByScheduleType,
      handleSuccess,
      handleError,
      setLoading,
    });
  };

  if (loading) {
    return <ComponentLoading />;
  }

  return (
    <BreakdownSchedule
      breakdownType='Full'
      company={company}
      schedule={schedule}
      scheduleDetails={scheduleDetails}
      saveSchedule={saveSchedule}
      deleteSchedule={deleteSchedule}
      openScheduleWithDelay={openScheduleWithDelay}
      canEdit={canEditBreakdownSchedule}
    />
  );
};

export default FullBreakdownPage;
